import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { submitContactLead } from "@/services/contactApi";
import {
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  HelpCircle,
  Plus,
  Minus,
  MessageSquare,
  DollarSign,
  Briefcase
} from "lucide-react";
import {
  FIELD_LIMITS,
  digitsOnly,
  emailError,
  firstInvalidName,
  phoneError,
  requiredText,
  selectError
} from "@/lib/formValidation";

const SERVICES_LIST = [
  "Website Development",
  "Shopify Store Development",
  "Mobile App Development",
  "UI/UX Design",
  "Custom Web Application Development",
  "Website SEO"
];

export default function Contact() {
  const [formData, setFormDetails] = useState({
    full_name: "",
    email: "",
    phone: "",
    company_name: "",
    service_needed: "",
    budget_range: "",
    project_details: ""
  });

  const formRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeFaq, setActiveFaq] = useState(null);
  const [searchParams] = useSearchParams();

  const budgetsList = [
    "Under $5,000",
    "$5,000 - $10,000",
    "$10,000 - $25,000",
    "$25,000 - $50,000",
    "$50,000+"
  ];

  const faqs = [
    {
      q: "What is your standard project delivery timeline?",
      a: "Our average landing page or custom website takes between 3 to 5 weeks. More complex e-commerce platforms and Custom SaaS dashboard applications average 6 to 12 weeks from strategy brief to production deployment."
    },
    {
      q: "Do you offer custom post-launch support and maintenance?",
      a: "Yes. We offer robust Service Level Agreements (SLAs) and monthly retainers providing hosting audits, speed optimization, and regular feature iteration sprints."
    },
    {
      q: "Do you provide a dedicated project manager during construction?",
      a: "Absolutely. Every client is assigned a senior technical project lead who runs weekly demo reviews, schedules sprint releases, and remains available on direct Slack channels."
    },
    {
      q: "How do you handle intellectual property (IP) and repositories?",
      a: "From day one, you own 100% of all visual designs, database architectures, and source code. All code is pushed directly to your organization's GitHub or GitLab."
    },
    {
      q: "What technologies do you typically recommend?",
      a: "We recommend proven modern stacks: React, Next.js, or TailwindCSS for frontend layouts, and FastAPI/Python or Node.js for backend automation. MongoDB or PostgreSQL serves as our main databases."
    },
    {
      q: "Can you integrate Shopify with our custom inventory systems?",
      a: "Yes, our e-commerce developers have extensive experience building customized Shopify apps and GraphQL connectors to sync third-party logistics and ERP warehousing."
    },
    {
      q: "What is your average budget threshold for new apps?",
      a: "While we consult and build simple websites under $5,000, our comprehensive SaaS application and advanced custom mobile platform projects typically begin at $10,000."
    },
    {
      q: "Do you sign Non-Disclosure Agreements (NDAs) before discovery?",
      a: "Yes. We hold confidentiality in absolute high regard. We routinely execute mutual NDAs prior to any technical briefing or database specification analysis."
    }
  ];

  useEffect(() => {
    const previous = document.title;
    document.title = "Contact | BizLogic Developers";
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    const requestedService = searchParams.get("service");
    if (requestedService && SERVICES_LIST.includes(requestedService)) {
      setFormDetails((prev) => ({ ...prev, service_needed: requestedService }));
    }
  }, [searchParams]);

  const contactFieldError = (name, value) => {
    if (name === "full_name") return requiredText(value, "Please enter your name.");
    if (name === "email") return emailError(value);
    if (name === "phone") return phoneError(value);
    if (name === "company_name") return requiredText(value, "Please enter your company name.");
    if (name === "service_needed" || name === "budget_range") return selectError(value);
    if (name === "project_details") return requiredText(value, "Please enter your project details.");
    return "";
  };

  const contactFieldOrder = [
    "full_name",
    "email",
    "phone",
    "company_name",
    "service_needed",
    "budget_range",
    "project_details"
  ];

  const handleInputChange = (e) => {
    const { name } = e.target;
    const value = name === "phone" ? digitsOnly(e.target.value) : e.target.value;
    setFormDetails((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((current) => (
      current[name] ? { ...current, [name]: contactFieldError(name, value) } : current
    ));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const next = name === "phone" ? digitsOnly(value) : value.trim();
    setFormDetails((prev) => ({ ...prev, [name]: next }));
    setFieldErrors((current) => ({ ...current, [name]: contactFieldError(name, next) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextForm = {
      ...formData,
      full_name: formData.full_name.trim(),
      email: formData.email.trim(),
      phone: digitsOnly(formData.phone),
      company_name: formData.company_name.trim(),
      service_needed: formData.service_needed.trim(),
      budget_range: formData.budget_range.trim(),
      project_details: formData.project_details.trim()
    };
    const nextErrors = Object.fromEntries(
      contactFieldOrder.map((name) => [name, contactFieldError(name, nextForm[name])])
    );
    setFormDetails(nextForm);
    setFieldErrors(nextErrors);
    const invalid = firstInvalidName(nextErrors, contactFieldOrder);
    if (invalid) {
      formRef.current?.querySelector(`[name="${invalid}"]`)?.focus();
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await submitContactLead(nextForm);
      setSubmitSuccess(true);
      setFormDetails({
        full_name: "",
        email: "",
        phone: "",
        company_name: "",
        service_needed: "",
        budget_range: "",
        project_details: ""
      });
      setFieldErrors({});
    } catch (err) {
      console.error(err);
      setErrorMessage(
        err instanceof Error ? err.message : "An unexpected network error occurred. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (idx) => {
    if (activeFaq === idx) {
      setActiveFaq(null);
    } else {
      setActiveFaq(idx);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Mesh background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-50 via-white to-white opacity-60 pointer-events-none -z-10 h-[800px]" />

      {/* TOP HEADER */}
      <section className="py-16 text-center max-w-4xl mx-auto px-6">
        <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 font-heading block mb-4">Start Your Growth</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 font-heading leading-tight mb-6">
          Connect With BizLogic
        </h1>
        <p className="text-slate-600 leading-relaxed text-sm sm:text-base max-w-xl mx-auto">
          Ready to build something extraordinary? Fill out our premium contact briefing form, and we'll draft a strategy roadmap within 24 business hours.
        </p>
      </section>

      {/* CONTACT FORM & INFO SECTION */}
      <section className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          
          {/* Left Block: Information Card */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="bg-slate-950 text-white rounded-3xl p-8 md:p-10 shadow-2xl border border-slate-900 relative overflow-hidden">
              <div className="absolute -bottom-12 -right-12 w-48 h-48 rounded-full bg-blue-600/10 blur-2xl pointer-events-none" />
              
              <h2 className="text-2xl font-bold font-heading mb-8">Contact Information</h2>
              
              <div className="flex flex-col gap-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-heading mb-1">Direct Inquiries</p>
                    <a href="mailto:bizlogicdeveloper@gmail.com" data-testid="contact-info-email" className="text-white hover:text-cyan-400 transition-colors font-medium">
                      bizlogicdeveloper@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-heading mb-1">Client Hotline</p>
                    <a href="tel:9083798171" data-testid="contact-info-phone" className="text-white hover:text-cyan-400 transition-colors font-medium">
                      908-379-8171
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-heading mb-1">HQ Address</p>
                    <p data-testid="contact-info-address" className="text-slate-300 font-medium text-sm leading-relaxed">
                      Old Bridge, NJ 08857
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Block: Contact Form */}
          <div className="lg:col-span-7 bg-white border border-slate-200/60 p-8 md:p-12 rounded-3xl shadow-xl shadow-slate-900/5">
            {submitSuccess ? (
              <div data-testid="contact-success-state" className="flex flex-col items-center text-center py-12 gap-6">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center border border-emerald-100">
                  <CheckCircle className="w-10 h-10 text-emerald-500" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-950 font-heading">
                  Inquiry Logged Successfully!
                </h2>
                <p className="text-slate-600 text-sm max-w-md leading-relaxed">
                  Thank you for submitting your brief. Our senior engineers will audit your system constraints and email you a customized proposal within 24 business hours.
                </p>
                <button
                  onClick={() => setSubmitSuccess(false)}
                  data-testid="contact-reset-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-6 py-3 rounded-xl mt-4 active:scale-95 transition-transform shadow-md"
                >
                  Submit Another Inquiry
                </button>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} data-testid="contact-form" className="flex flex-col gap-6" noValidate>
                <h2 className="text-2xl font-bold font-heading text-slate-950 mb-2">Project Brief Form</h2>
                
                {errorMessage && (
                  <div data-testid="contact-error-message" className="bg-red-50 border border-red-100 text-red-600 text-xs rounded-xl p-4 font-medium leading-relaxed">
                    {errorMessage}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="full_name" className="text-xs font-bold text-slate-600 uppercase tracking-wider font-heading">Full Name</label>
                    <input
                      type="text"
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      maxLength={FIELD_LIMITS.name}
                      aria-invalid={Boolean(fieldErrors.full_name)}
                      aria-describedby={fieldErrors.full_name ? "full_name-error" : undefined}
                      data-testid="contact-form-name-input"
                      placeholder="Jane Doe"
                      className="border border-slate-200/80 hover:border-slate-300 rounded-xl px-4 py-3.5 text-sm text-slate-900 bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                    {fieldErrors.full_name ? <p id="full_name-error" role="alert" className="text-xs text-red-600 font-medium">{fieldErrors.full_name}</p> : null}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="text-xs font-bold text-slate-600 uppercase tracking-wider font-heading">Business Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      maxLength={FIELD_LIMITS.email}
                      aria-invalid={Boolean(fieldErrors.email)}
                      aria-describedby={fieldErrors.email ? "email-error" : undefined}
                      data-testid="contact-form-email-input"
                      placeholder="jane@company.com"
                      className="border border-slate-200/80 hover:border-slate-300 rounded-xl px-4 py-3.5 text-sm text-slate-900 bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                    {fieldErrors.email ? <p id="email-error" role="alert" className="text-xs text-red-600 font-medium">{fieldErrors.email}</p> : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="phone" className="text-xs font-bold text-slate-600 uppercase tracking-wider font-heading">Phone Number</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      pattern="[0-9]{10}"
                      aria-invalid={Boolean(fieldErrors.phone)}
                      aria-describedby={fieldErrors.phone ? "phone-error" : undefined}
                      data-testid="contact-form-phone-input"
                      placeholder="4155550199"
                      className="border border-slate-200/80 hover:border-slate-300 rounded-xl px-4 py-3.5 text-sm text-slate-900 bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                    {fieldErrors.phone ? <p id="phone-error" role="alert" className="text-xs text-red-600 font-medium">{fieldErrors.phone}</p> : null}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="company_name" className="text-xs font-bold text-slate-600 uppercase tracking-wider font-heading">Company Name</label>
                    <input
                      type="text"
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      maxLength={FIELD_LIMITS.company}
                      aria-invalid={Boolean(fieldErrors.company_name)}
                      aria-describedby={fieldErrors.company_name ? "company_name-error" : undefined}
                      data-testid="contact-form-company-input"
                      placeholder="Innovate Inc."
                      className="border border-slate-200/80 hover:border-slate-300 rounded-xl px-4 py-3.5 text-sm text-slate-900 bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                    {fieldErrors.company_name ? <p id="company_name-error" role="alert" className="text-xs text-red-600 font-medium">{fieldErrors.company_name}</p> : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="service_needed" className="text-xs font-bold text-slate-600 uppercase tracking-wider font-heading">Service Needed</label>
                    <select
                      id="service_needed"
                      name="service_needed"
                      value={formData.service_needed}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      aria-invalid={Boolean(fieldErrors.service_needed)}
                      aria-describedby={fieldErrors.service_needed ? "service_needed-error" : undefined}
                      data-testid="contact-form-service-select"
                      className="border border-slate-200/80 hover:border-slate-300 rounded-xl px-4 py-3.5 text-sm text-slate-900 bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    >
                      <option value="" disabled>Select a core service</option>
                      {SERVICES_LIST.map((srv, idx) => (
                        <option key={idx} value={srv}>{srv}</option>
                      ))}
                    </select>
                    {fieldErrors.service_needed ? <p id="service_needed-error" role="alert" className="text-xs text-red-600 font-medium">{fieldErrors.service_needed}</p> : null}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="budget_range" className="text-xs font-bold text-slate-600 uppercase tracking-wider font-heading">Budget Range</label>
                    <select
                      id="budget_range"
                      name="budget_range"
                      value={formData.budget_range}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      aria-invalid={Boolean(fieldErrors.budget_range)}
                      aria-describedby={fieldErrors.budget_range ? "budget_range-error" : undefined}
                      data-testid="contact-form-budget-select"
                      className="border border-slate-200/80 hover:border-slate-300 rounded-xl px-4 py-3.5 text-sm text-slate-900 bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    >
                      <option value="" disabled>Select your budget</option>
                      {budgetsList.map((bud, idx) => (
                        <option key={idx} value={bud}>{bud}</option>
                      ))}
                    </select>
                    {fieldErrors.budget_range ? <p id="budget_range-error" role="alert" className="text-xs text-red-600 font-medium">{fieldErrors.budget_range}</p> : null}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="project_details" className="text-xs font-bold text-slate-600 uppercase tracking-wider font-heading">Project Details</label>
                  <textarea
                    id="project_details"
                    name="project_details"
                    value={formData.project_details}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    maxLength={FIELD_LIMITS.message}
                    rows={4}
                    aria-invalid={Boolean(fieldErrors.project_details)}
                    aria-describedby={fieldErrors.project_details ? "project_details-error" : undefined}
                    data-testid="contact-form-details-textarea"
                    placeholder="Describe your goals, tech preferences, and any important integration milestones..."
                    className="border border-slate-200/80 hover:border-slate-300 rounded-xl px-4 py-3.5 text-sm text-slate-900 bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                  />
                  {fieldErrors.project_details ? <p id="project_details-error" role="alert" className="text-xs text-red-600 font-medium">{fieldErrors.project_details}</p> : null}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-form-submit-btn"
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-4 rounded-xl shadow-lg mt-4 active:scale-95 transition-all text-center"
                >
                  {isSubmitting ? "Logging Inquiry..." : "Submit Project Brief"}
                </button>
              </form>
            )}
          </div>

        </div>
      </section>

      {/* FAQ SECTION (MINIMUM 8 ACCORDIONS) */}
      <section className="py-20 lg:py-32 bg-slate-50 border-t border-slate-100" id="faq-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col items-center text-center gap-4 mb-16 md:mb-20">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 font-heading">Common Queries</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading tracking-tight">
              Frequently Asked Questions
            </h2>
            <div className="w-16 h-1.5 bg-blue-600 rounded-full mt-2" />
          </div>

          <div className="flex flex-col gap-4" data-testid="faq-accordion-container">
            {faqs.map((faq, idx) => {
              const isOpen = activeFaq === idx;
              return (
                <div
                  key={idx}
                  data-testid={`faq-item-${idx}`}
                  className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden shadow-sm transition-shadow duration-200"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    data-testid={`faq-trigger-${idx}`}
                    className="w-full flex items-center justify-between p-6 text-left focus:outline-none group"
                  >
                    <span className="font-bold text-slate-900 text-sm sm:text-base font-heading group-hover:text-blue-600 transition-colors flex items-center gap-3">
                      <HelpCircle className="w-5 h-5 text-slate-400 shrink-0 group-hover:text-blue-500" />
                      {faq.q}
                    </span>
                    <span className="text-slate-400 shrink-0 p-1 bg-slate-100 group-hover:bg-blue-50 rounded-lg transition-colors ml-4">
                      {isOpen ? <Minus className="w-4 h-4 text-blue-600" /> : <Plus className="w-4 h-4 text-slate-600" />}
                    </span>
                  </button>

                  <div
                    data-testid={`faq-content-${idx}`}
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? "max-h-60 border-t border-slate-100" : "max-h-0"
                    }`}
                  >
                    <div className="p-6 text-slate-600 text-sm leading-relaxed bg-slate-50/50">
                      {faq.a}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
